import React from 'react';
import { Accordion, AccordionButton, AccordionItem, AccordionPanel } from '@reach/accordion';
import { FaChevronDown } from 'react-icons/fa';

/**
 * A plain-language map of what INNA can and cannot do. It is collapsed by
 * default so returning users keep the full conversation area, while people
 * meeting the assistant for the first time have help in a predictable place.
 */
const ChatHelp = () => (
    <Accordion collapsible className="chat-help">
        <AccordionItem className="chat-help-item">
            <h2 className="chat-help-heading">
                <AccordionButton className="chat-help-toggle">
                    <span className="chat-help-question" aria-hidden="true">
                        ?
                    </span>
                    <span className="chat-help-toggle-copy">
                        <span className="chat-help-title">What can INNA help with?</span>
                        <span className="chat-help-summary">Capabilities, tips and important limits</span>
                    </span>
                    <FaChevronDown className="chat-help-chevron" aria-hidden="true" />
                </AccordionButton>
            </h2>

            <AccordionPanel className="chat-help-panel">
                <p className="chat-help-lead">
                    Ask a question in everyday language. Adding a period, partner, publisher, territory or title
                    usually produces a more useful answer.
                </p>

                <ul className="chat-help-capabilities">
                    <li>
                        <strong>Catalogue and rights</strong>
                        <span>
                            Find and count titles by author, narrator, publisher, language or genre, and check
                            territories, rights, embargoes and synthetic narration.
                        </span>
                    </li>
                    <li>
                        <strong>Sales and trends</strong>
                        <span>
                            Rank titles, authors, publishers or partners and compare unit sales across accounting
                            periods, territories, business models and promotions.
                        </span>
                    </li>
                    <li>
                        <strong>Products and availability</strong>
                        <span>
                            Explore configured product windows, partners and business models, including what is
                            starting, active or ending.
                        </span>
                    </li>
                    <li>
                        <strong>Licensors and contracts</strong>
                        <span>
                            Check licensor status, payee setup, missing or expiring royalty contracts and referral
                            partners.
                        </span>
                    </li>
                    <li>
                        <strong>Audio operations</strong>
                        <span>
                            Investigate recorded file coverage and technical metadata such as duration, format and
                            bitrate.
                        </span>
                    </li>
                    <li>
                        <strong>Sources and method</strong>
                        <span>Ask how an answer was reached or request the supporting SQL for a technical view.</span>
                    </li>
                </ul>

                <div className="chat-help-limits">
                    <h3>Good to know</h3>
                    <ul>
                        <li>INNA is read-only and cannot change source or warehouse data.</li>
                        <li>
                            It is designed for quick insights, not bulk reports or exports, and returns at most 100
                            rows.
                        </li>
                        <li>
                            It can rank and compare using financial data, but cannot disclose exact revenue, prices,
                            royalties or payment amounts.
                        </li>
                        <li>
                            BigBobby is a reporting copy refreshed by separate jobs, so its data may lag behind the
                            source systems.
                        </li>
                    </ul>
                </div>

                <p className="chat-help-warning" role="note">
                    <strong>Research and discovery only.</strong> Never use INNA&apos;s answers or data in any context
                    until you have independently verified their accuracy against the relevant source system.
                </p>
            </AccordionPanel>
        </AccordionItem>
    </Accordion>
);

export default ChatHelp;
